import {useState, createContext, useCallback, useMemo, useEffect, useContext} from "react";
import PropTypes from "prop-types";
import {useLocation, useNavigate} from "react-router-dom";

// Utils
import {exists} from "../utils/helpers";

// Hooks
import {useSocket} from "./socket";
import useMountedState from "../hooks/useMountedState";

// Contexts
import {NavContext} from "./nav";
import {AuthContext} from "./auth";

// Socket Constants
import {
  FACILITY_LOCK,
  LISTEN_NOTIFY_FACILITY_UPDATE,
  REQUEST_FACILITY_LOCK
} from "../pages/general/Room";

export const FacilityNavContext = createContext();

const FacilityNavProvider = ({children}) => {
  const isMounted = useMountedState();

  const [options, setOptions] = useState({});
  const [facility, setFacility] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [allOrder, setAllOrder] = useState(null);
  const [lock, setLock] = useState();
  const [componentsChanged, setComponentsChanged] = useState({});

  const navigate = useNavigate();
  const socket = useSocket();

  const {intermediateAction} = useContext(NavContext);
  const {currentUser} = useContext(AuthContext);

  const available = useMemo(() => Object.keys(options).filter(key => options[key].rule), [options]);

  const location = useLocation();

  useEffect(() => {
    if (
      exists(facility?.id) &&
      exists(lock) &&
      facility.id === Number(lock) &&
      currentUser?.type?.name !== "super"
    )
      navigate("/facilities");
  }, [facility, lock, navigate, currentUser]);

  const handleLock = useCallback(({id: facilityId}) => {
    setLock(facilityId);
  }, []);

  useEffect(() => {
    socket.on(FACILITY_LOCK, handleLock);

    if (isMounted() && currentUser && currentUser.publicId) {
      socket.emit(REQUEST_FACILITY_LOCK);
    }

    return () => {
      // unbind all event handlers used in this component
      socket.off(FACILITY_LOCK, handleLock);
    };
  }, [isMounted, currentUser, socket, handleLock]);

  // TODO: somehow prevent update when on facility builder (if changes are queued or elements are locked)
  // will need to then resolve these differences
  const handleFacilityChange = useCallback(
    ({id, builder: newBuilder, components, user}) => {
      if (id === facility?.id) {
        if (newBuilder) setFacility(prev => (prev ? {...prev, builder: newBuilder} : prev));
        if (components && user !== currentUser.publicId)
          setComponentsChanged(prev => ({...prev, ...components}));
      }
    },
    [facility, currentUser]
  );

  useEffect(() => {
    socket.on(LISTEN_NOTIFY_FACILITY_UPDATE, handleFacilityChange);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LISTEN_NOTIFY_FACILITY_UPDATE, handleFacilityChange);
    };
  }, [isMounted, socket, handleFacilityChange]);

  useEffect(() => {
    const facilitySlug = location.pathname.replace("/facilities").split("/")[1];
    if (facility && facilitySlug !== facility.slug) setFacility(null);
  }, [facility, location]);

  const addOptions = useCallback(newOptions => setOptions(prev => ({...prev, ...newOptions})), []);

  const removeOptions = useCallback(
    names => {
      const temp = {...options};
      names.map(name => delete temp[name]);
      setOptions(temp);
    },
    [options]
  );

  // Make the provider update only when it should.
  // We only want to force re-renders on dependencies
  const memoedValue = useMemo(
    () => ({
      facility,
      setFacility,
      options,
      addOptions,
      removeOptions,
      available,
      analytics,
      setAnalytics,
      allOrder,
      setAllOrder,
      intermediateAction,
      componentsChanged,
      setComponentsChanged
    }),
    [
      facility,
      options,
      addOptions,
      removeOptions,
      available,
      analytics,
      allOrder,
      intermediateAction,
      componentsChanged
    ]
  );

  return <FacilityNavContext.Provider value={memoedValue}>{children}</FacilityNavContext.Provider>;
};

FacilityNavProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default FacilityNavProvider;
